import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SearchBox from "./SearchBox";

interface NavbarProps {
  transparentTop?: boolean;
}

const navItems = [
  { label: "首页", to: "/" },
  { label: "电视剧", to: "/category" },
  { label: "资讯", to: "/news" },
  { label: "关于", to: "/about" },
];

export default function Navbar({ transparentTop = false }: NavbarProps) {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = !transparentTop || scrolled || menuOpen;

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? "border-b border-background-200/80 bg-background-50/95 backdrop-blur"
          : "bg-transparent"
      }`}
    >
      <div
        className={`flex h-16 w-full items-center justify-between gap-4 px-4 md:h-20 md:px-6 lg:px-10 ${
          solid ? "text-foreground-900" : "text-background-50"
        }`}
      >
        <Link to="/" className="flex items-center gap-2.5">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md ${
              solid ? "bg-primary-500 text-background-50" : "bg-accent-500 text-primary-950"
            }`}
          >
            <i className="ri-sword-line text-lg"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-wide md:text-xl">
              仙剑奇侠传
            </span>
            <span className="text-[10px] tracking-[0.3em] opacity-70">XIANJIAN</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? solid
                    ? "bg-primary-50 text-primary-700"
                    : "bg-background-50/15 text-accent-300"
                  : solid
                  ? "hover:bg-background-100 hover:text-primary-700"
                  : "hover:bg-background-50/15"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <SearchBox />
          </div>
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md lg:hidden"
          >
            <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 text-foreground-900 lg:hidden">
          <div className="mb-4 md:hidden">
            <SearchBox variant="panel" onNavigate={() => setMenuOpen(false)} />
          </div>
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center justify-between rounded-md px-4 py-3 text-sm font-medium ${
                  isActive(item.to)
                    ? "bg-primary-50 text-primary-700"
                    : "hover:bg-background-100"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}